/**
 * Credit allocation down the channel.
 *
 * ADMINISTRATIVE REACH
 * Moving credits to a reseller or customer beneath you is commercial
 * administration of your own channel. It needs the `credit.adjust` capability
 * and the recipient being beneath you in the tree — and it deliberately does
 * NOT need the recipient's consent, which governs access to their compliance
 * data and nothing else (see api/src/db/scope.ts, "two kinds of reach"). So the
 * recipient list comes from the channel endpoint, which uses the same reach,
 * and is never filtered by consent.
 *
 * WHAT THE SERVER CHECKS AND WHAT THIS SCREEN CHECKS
 * The server refuses an over-allocation, a transfer past a trial plan's cap, a
 * recipient outside the channel and a balance that would go negative. This
 * screen mirrors those limits so the mistake cannot be submitted in the first
 * place — but that is a courtesy. The server has the final say, and its refusal
 * is shown in full if the two ever disagree (a balance that changed while the
 * dialog was open, say).
 *
 * THE OPERATOR
 * The platform operator mints credits rather than transferring its own, so its
 * balance does not change and is not a limit. The preview says so.
 *
 * NO IDEMPOTENCY KEY
 * The API has none, so a request that may have reached the server but got no
 * answer is NOT offered for a blind retry: the dialog tells the person to check
 * the ledger first.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { TRIAL_CREDIT_CAP, MAX_CREDIT_BALANCE } from '@opencomply/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton, ErrorNotice } from './ui';
import { IconOrg } from './icons';
import {
  useCreditsT, useCreditsError,
  type ChannelOrganization, type ChannelResponse, type CreditsKey,
} from './creditsShared';

const NOTE_MAX = 500;
const PAGE_STEP = 10;
const MAX_INDENT = 3;

export function CreditAllocation({ balance, channel }: {
  /** This organisation's balance; undefined while loading or after a failure. */
  balance: number | undefined;
  channel: UseQueryResult<ChannelResponse>;
}) {
  const { tx, tc, tier, credits, num } = useCreditsT();
  const { session } = useAuth();
  const isOperator = session?.organization.tier === 'SUPERADMIN';

  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE_STEP);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const recipients = useMemo(
    () => (channel.data?.items ?? []).filter((o) => !o.isSelf),
    [channel.data],
  );
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((o) => `${o.name} ${o.domain ?? ''}`.toLowerCase().includes(q));
  }, [recipients, search]);

  const active = recipients.find((o) => o.id === activeId) ?? null;
  // Stable identity: the dialog re-focuses itself whenever its onClose changes.
  const closeModal = useCallback(() => setActiveId(null), []);

  return (
    <Card>
      <CardHeader
        title={tx('credits.alloc.title')}
        subtitle={isOperator ? tx('credits.alloc.subtitleOperator') : tx('credits.alloc.subtitle')}
      />

      {notice && (
        <div role="status"
             className="mb-4 rounded-xl border border-good/30 bg-good/[0.06] px-4 py-3 flex items-start justify-between gap-3">
          <p className="text-sm text-ink">{notice}</p>
          <button className="text-sm font-medium text-brand hover:underline shrink-0"
                  onClick={() => setNotice(null)}>
            {tx('credits.alloc.dismiss')}
          </button>
        </div>
      )}

      {channel.isPending ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : channel.isError ? (
        <ErrorNotice message={tx('credits.alloc.loadFailed')} onRetry={() => void channel.refetch()}
                     retryLabel={tc('common.retry')} />
      ) : recipients.length === 0 ? (
        <EmptyState icon={<IconOrg className="w-10 h-10" />}
                    title={tx('credits.alloc.none')} description={tx('credits.alloc.noneHint')} />
      ) : (
        <div className="space-y-3">
          {channel.data?.truncated && (
            <ErrorNotice message={tx('credits.alloc.truncated', { count: num(channel.data.items.length) })} />
          )}

          {recipients.length > PAGE_STEP && (
            <input className="oc-input sm:max-w-xs" type="search" value={search}
                   placeholder={tx('credits.alloc.search')} aria-label={tx('credits.alloc.search')}
                   onChange={(e) => { setSearch(e.target.value); setShown(PAGE_STEP); }} />
          )}

          {matches.length === 0 ? (
            <p className="oc-muted py-4 text-center">{tx('credits.alloc.noMatch')}</p>
          ) : (
            <ul className="space-y-2">
              {matches.slice(0, shown).map((org) => (
                <li key={org.id}
                    style={{ marginInlineStart: `${Math.min(Math.max(org.depth - 1, 0), MAX_INDENT) * 0.75}rem` }}>
                  <div className={cn(
                    'rounded-xl border border-line p-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2',
                    !org.isActive && 'opacity-70',
                  )}>
                    <div className="min-w-0">
                      <p className="font-medium text-ink break-words">{org.name}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Pill tone="neutral">{tier(org.tier)}</Pill>
                        {org.plan === 'TRIAL' && <Pill tone="warning">{tx('credits.alloc.trial')}</Pill>}
                        {!org.isActive && <Pill tone="serious">{tx('credits.alloc.inactive')}</Pill>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-auto">
                      <p className="text-sm text-ink-secondary text-right">
                        <span className="text-xs text-ink-muted block">{tx('credits.alloc.holds')}</span>
                        <span className="tabular-nums font-medium text-ink">{credits(org.creditBalance)}</span>
                      </p>
                      <Button size="sm" variant="primary"
                              aria-label={tx('credits.alloc.buttonFor', { name: org.name })}
                              onClick={() => { setNotice(null); setActiveId(org.id); }}>
                        {tx('credits.alloc.button')}
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {matches.length > shown && (
            <div className="text-center">
              <Button size="sm" onClick={() => setShown((n) => n + PAGE_STEP)}>
                {tx('credits.alloc.showMore')}
              </Button>
            </div>
          )}
        </div>
      )}

      {active && (
        <AllocateModal
          // Keyed so every recipient opens with a clean form.
          key={active.id}
          org={active}
          sourceBalance={balance}
          isOperator={!!isOperator}
          onClose={closeModal}
          onDone={(message) => { setActiveId(null); setNotice(message); }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

type Mode = 'give' | 'take';

type Parsed =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'number'; value: number };

/** Digits only. No signs, decimals, exponents, separators or spaces. */
function parseAmount(text: string): Parsed {
  const trimmed = text.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (!/^\d{1,7}$/.test(trimmed)) return { kind: 'invalid' };
  return { kind: 'number', value: Number(trimmed) };
}

function AllocateModal({ org, sourceBalance, isOperator, onClose, onDone }: {
  org: ChannelOrganization;
  sourceBalance: number | undefined;
  isOperator: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { tx, tc, num, credits } = useCreditsT();
  const describeError = useCreditsError();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>('give');
  const [amountText, setAmountText] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The request may have been applied; do not offer a second attempt.
  const [unsure, setUnsure] = useState(false);

  // --- Limits ------------------------------------------------------------------

  const cap = org.plan === 'TRIAL' ? TRIAL_CREDIT_CAP : MAX_CREDIT_BALANCE;
  const room = Math.max(0, cap - org.creditBalance);

  /** The largest amount that can go through, and what sets it. */
  const limit = (() => {
    if (mode === 'take') return { max: org.creditBalance, bound: 'theirs' as const };
    const bySource = isOperator ? Infinity : (sourceBalance ?? 0);
    return bySource <= room
      ? { max: bySource, bound: 'source' as const }
      : { max: room, bound: 'cap' as const };
  })();

  // A regular organisation cannot allocate without knowing its own balance.
  const balanceUnknown = !isOperator && sourceBalance === undefined;

  const parsed = parseAmount(amountText);
  const amount = parsed.kind === 'number' ? parsed.value : null;

  const problem: string | null = (() => {
    if (parsed.kind === 'empty') return null; // not an error yet, just unfinished
    if (mode === 'give' && balanceUnknown) return null; // explained by its own notice below
    if (parsed.kind === 'invalid') return tx('credits.alloc.errNotWhole');
    if (parsed.value < 1) return tx('credits.alloc.errZero');
    if (parsed.value <= limit.max) return null;
    if (mode === 'take') {
      return limit.max === 0
        ? tx('credits.alloc.errNothingToReclaim', { name: org.name })
        : tx('credits.alloc.errExceedsTheirs', { name: org.name, available: num(limit.max) });
    }
    if (limit.bound === 'cap') {
      return limit.max === 0
        ? tx('credits.alloc.errAtCap', { name: org.name, cap: num(cap) })
        : tx('credits.alloc.errExceedsCap', { name: org.name, cap: num(cap), max: num(limit.max) });
    }
    return limit.max === 0
      ? tx('credits.alloc.errNoCredits')
      : tx('credits.alloc.errExceedsSource', { available: num(limit.max) });
  })();

  const valid = amount !== null && problem === null && !balanceUnknown;

  // --- Preview -----------------------------------------------------------------

  const theirAfter = valid ? org.creditBalance + (mode === 'give' ? amount : -amount) : null;
  const yourAfter = valid && !isOperator && sourceBalance !== undefined
    ? sourceBalance + (mode === 'give' ? -amount : amount)
    : null;

  // --- Submit ------------------------------------------------------------------

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['credits'] });
    void queryClient.invalidateQueries({ queryKey: ['organizations'] });
  };

  // A synchronous lock. `transfer.isPending` only flips after the next render,
  // so two clicks (or a held Enter key) landing in the same tick would both
  // pass a check on it — and this API has no idempotency key to absorb the
  // second one. The ref closes that gap.
  const inFlight = useRef(false);

  const transfer = useMutation({
    mutationFn: (delta: number) =>
      api.post<{ targetBalance: number; sourceBalance?: number }>('/credits/adjust', {
        targetOrganizationId: org.id,
        delta,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (result, delta) => {
      refresh();
      const n = Math.abs(delta);
      onDone(tx(delta > 0 ? 'credits.alloc.doneGive' : 'credits.alloc.doneTake', {
        count: n, amount: num(n), name: org.name, balance: num(result.targetBalance),
      }));
    },
    onSettled: () => { inFlight.current = false; },
    onError: (err) => {
      const { message, unknownOutcome } = describeError(err);
      setError(message);
      if (unknownOutcome) setUnsure(true);
      // Either way the numbers on screen may now be wrong.
      refresh();
    },
  });

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || amount === null || inFlight.current || transfer.isPending || unsure) return;
    inFlight.current = true;
    setError(null);
    transfer.mutate(mode === 'give' ? amount : -amount);
  };

  // A refusal belongs to the attempt that caused it; switching mode starts over.
  useEffect(() => { setError(null); }, [mode]);

  // The dialog must not be dismissable mid-request, but it must also hand the
  // Modal a STABLE onClose: Modal re-focuses itself whenever that changes,
  // which would pull the cursor out of the amount field on every keystroke.
  const pendingRef = useRef(false);
  pendingRef.current = transfer.isPending;
  const guardedClose = useCallback(() => {
    if (!pendingRef.current) onClose();
  }, [onClose]);

  const title = tx('credits.alloc.modalTitle', { name: org.name });
  const confirmLabel = valid && amount !== null
    ? tx(mode === 'give' ? 'credits.alloc.confirmGive' : 'credits.alloc.confirmTake',
        { count: amount, amount: num(amount) })
    : tx(mode === 'give' ? 'credits.alloc.modeGive' : 'credits.alloc.modeTake');

  const amountId = 'credit-amount';
  const noteId = 'credit-note';

  return (
    <Modal
      open
      onClose={guardedClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={transfer.isPending}>
            {unsure ? tc('common.close') : tc('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" form="credit-transfer-form"
                  disabled={!valid || transfer.isPending || unsure}>
            {transfer.isPending ? tx('credits.alloc.working') : confirmLabel}
          </Button>
        </>
      }
    >
      <form id="credit-transfer-form" onSubmit={submit} noValidate>
        <p className="text-sm text-ink-secondary mb-4">
          <span className="font-medium text-ink">{org.name}</span>
          {' · '}{credits(org.creditBalance)}
        </p>

        <div className="mb-4">
          <span className="oc-label" id="credit-mode-label">{tx('credits.alloc.action')}</span>
          <div role="radiogroup" aria-labelledby="credit-mode-label"
               className="grid grid-cols-2 gap-1 rounded-xl bg-ink/[0.06] p-1">
            {(['give', 'take'] as const).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={mode === m}
                      disabled={transfer.isPending || unsure}
                      onClick={() => { setMode(m); setAmountText(''); }}
                      className={cn(
                        'min-h-[2.5rem] rounded-lg text-sm font-medium transition-colors',
                        mode === m ? 'bg-surface-raised text-ink shadow-sm' : 'text-ink-secondary hover:text-ink',
                      )}>
                {tx(m === 'give' ? 'credits.alloc.modeGive' : 'credits.alloc.modeTake')}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-ink-muted">
            {mode === 'give'
              ? tx(isOperator ? 'credits.alloc.modeGiveHintOperator' : 'credits.alloc.modeGiveHint', { name: org.name })
              : tx(isOperator ? 'credits.alloc.modeTakeHintOperator' : 'credits.alloc.modeTakeHint', { name: org.name })}
          </p>
        </div>

        <Field
          label={tx('credits.alloc.amount')}
          htmlFor={amountId}
          hint={tx('credits.alloc.amountHint')}
          error={problem ?? undefined}
        >
          <div className="flex gap-2">
            <input
              id={amountId}
              className="oc-input tabular-nums"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={7}
              value={amountText}
              disabled={transfer.isPending || unsure}
              aria-invalid={problem !== null}
              onChange={(e) => { setAmountText(e.target.value); setError(null); }}
            />
            <Button
              disabled={transfer.isPending || unsure || !Number.isFinite(limit.max) || limit.max < 1}
              aria-label={tx('credits.alloc.useMax', { amount: num(limit.max) })}
              onClick={() => { setAmountText(String(limit.max)); setError(null); }}
            >
              {Number.isFinite(limit.max) ? tx('credits.alloc.max', { amount: num(limit.max) }) : tx('credits.alloc.max', { amount: '—' })}
            </Button>
          </div>
        </Field>

        {mode === 'give' && org.plan === 'TRIAL' && (
          <p className="-mt-2 mb-4 text-xs text-ink-muted">
            {tx('credits.alloc.trialCap', { name: org.name, cap: num(TRIAL_CREDIT_CAP) })}
          </p>
        )}

        <Field label={tx('credits.alloc.note')} htmlFor={noteId}
               hint={tx('credits.alloc.noteHint', { used: num(note.length), max: num(NOTE_MAX) })}>
          <textarea id={noteId} className="oc-input py-2 min-h-[4.5rem]" rows={2}
                    maxLength={NOTE_MAX} value={note} disabled={transfer.isPending || unsure}
                    onChange={(e) => setNote(e.target.value)} />
        </Field>

        {/* The preview: what both balances will be if this goes through. */}
        <div className="rounded-xl bg-ink/[0.04] px-3 py-3" aria-live="polite">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-muted mb-2">
            {tx('credits.alloc.preview')}
          </p>
          {valid && theirAfter !== null ? (
            <dl className="space-y-1.5 text-sm">
              <BalanceLine
                label={tx('credits.alloc.yourBalance')}
                before={isOperator ? null : sourceBalance ?? null}
                after={yourAfter}
                unchangedText={isOperator ? tx('credits.alloc.unchanged') : undefined}
                fmt={num} tx={tx}
              />
              <BalanceLine label={org.name} before={org.creditBalance} after={theirAfter} fmt={num} tx={tx} />
            </dl>
          ) : (
            <p className="text-sm text-ink-muted">{tx('credits.alloc.previewEmpty')}</p>
          )}
        </div>

        {yourAfter === 0 && (
          // Not an error — the server allows it — but rarely what was meant.
          <p className="mb-4 text-sm text-warning">{tx('credits.alloc.leavesZero')}</p>
        )}

        {mode === 'give' && balanceUnknown && (
          <div className="mb-4"><ErrorNotice message={tx('credits.balance.loadFailed')} /></div>
        )}

        {error && (
          <div className="mt-4">
            <ErrorNotice message={error} />
          </div>
        )}
      </form>
    </Modal>
  );
}

function BalanceLine({ label, before, after, unchangedText, fmt, tx }: {
  label: string;
  before: number | null;
  after: number | null;
  unchangedText?: string;
  fmt: (n: number) => string;
  tx: (key: CreditsKey, options?: Record<string, unknown>) => string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-secondary min-w-0 break-words">{label}</dt>
      <dd className="tabular-nums text-right shrink-0">
        {unchangedText ? (
          <span className="text-ink-muted">{unchangedText}</span>
        ) : (
          <>
            {before !== null && <span className="text-ink-muted">{fmt(before)}</span>}
            {before !== null && after !== null && <span className="text-ink-muted" aria-hidden> → </span>}
            {before !== null && after !== null && <span className="sr-only"> {tx('credits.alloc.becomes')} </span>}
            {after !== null && <span className="font-semibold text-ink">{fmt(after)}</span>}
          </>
        )}
      </dd>
    </div>
  );
}
